import { useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { useCanManageGbp } from '@/hooks/use-can-manage-gbp';
import { locationApi } from '@/lib/api-paths';
import { TERMS } from '@pageboss/shared';
import { buttonVariants } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// The connected screen's way out (design §5.4 / D2's privacy promise).
//
// DELETE /api/gbp/:siteId/connection existed from Task 12, but its only UI
// caller was the LOCKED (tier-downgraded) panel — so a paying, connected
// customer had no disconnect control at all, while the privacy policy
// shipped on this branch names disconnection as the trigger that deletes
// stored reviewer names. The policy promised an action nobody could take.
//
// Deliberately quiet: a bordered footer row, small outline button, no card
// frame and no sage. Sage is reserved for the primary action and the
// "everything is fine" summary (design brief §3), and a destructive action
// nobody performs twice a year must not compete with the screen's real
// content. window.confirm is the established destructive-confirm pattern in
// this dashboard (site archive, page delete, domain removal, mailbox
// removal) — it is keyboard- and screen-reader-native, which a hand-rolled
// panel would have to re-earn.
//
// The confirm copy states exactly what the privacy policy promises, and
// nothing more: we stop showing the Google data, and the reviewer names we
// stored are deleted. Keep these two clauses in step with
// apps/marketing/src/pages/Privacy.tsx — if one changes, the other is wrong.
//
// It deliberately does NOT promise "and we'll revoke our access at Google."
// We do attempt that (revokeAndPurgeConnection step 4), but it is
// best-effort by design: the whole point of that try/catch is that failing to
// revoke must never block deleting the customer's data. It is also skipped
// outright when GBP_TOKEN_KEY is absent or rotated, because
// gbpConnectionDepsFromEnv passes an empty key rather than throwing. So the
// revoke can silently not happen, the endpoint 204s either way, and a
// privacy-minded owner who then checks Google's "third-party access" list
// would find us still there. The policy makes no revocation claim; neither
// will this dialog.
const CONFIRM_COPY = [
  `Disconnect your ${TERMS.googleProfile}?`,
  '',
  'pageboss will stop showing your Google data on this screen, and the reviewer names we stored will be deleted.',
  '',
  'You can connect again any time.',
].join('\n');

export function GbpDisconnectRow({
  siteId,
  onDisconnected,
}: {
  siteId: string;
  onDisconnected: (announcement: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Disconnecting is owner-only (gbp:manage). A manager or editor still sees
  // the connected data above; they just get no way out they could not take.
  const canManage = useCanManageGbp();

  async function disconnect() {
    if (!window.confirm(CONFIRM_COPY)) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(locationApi(businessId, siteId, 'gbp/connection'));
      // The 204 is authoritative: the reviews are already gone from D1 and
      // the cache is purged. The parent switches the screen to
      // `not_connected` on the strength of THAT, not on the strength of the
      // follow-up refetch — otherwise a network blip on the refetch would
      // leave the location card, the performance tiles and the reviewer
      // names still on screen after they had been deleted, making the
      // dialog's first promise false at the exact moment it was kept.
      // `done` covers this component's own corner of that case.
      setDone(true);
      onDisconnected(
        `${TERMS.googleProfile} disconnected. The reviews we stored have been deleted.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disconnect right now.');
      setBusy(false);
    }
  }

  if (!canManage) return null;

  return (
    <div className="flex flex-col gap-3 border-t border-border-subtle pt-6">
      {done ? (
        <p role="status" className="text-sm text-text-secondary">
          Disconnected. The reviews we stored have been deleted.
        </p>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-prose text-sm text-text-secondary">
            Disconnecting stops pageboss from showing your Google data here and deletes the reviews
            we stored, including reviewer names.
          </p>
          <button
            type="button"
            onClick={() => void disconnect()}
            disabled={busy}
            className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'shrink-0')}
          >
            {busy ? 'Disconnecting…' : `Disconnect ${TERMS.googleProfile}`}
          </button>
        </div>
      )}
      {error ? (
        <p className="text-sm text-danger-fg" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
